// ./src/components/features/CreateEventWizard/steps/MediaStep.tsx
//
// Step 5 — "Make it yours" (media). Cover photo + cover video are chosen through
// the shared MediaSelector (library / upload+edit / external URL; Unsplash &
// GIPHY are Stage 2). Picks persist to the wizard draft (coverImage/coverVideo), and
// the event (created when the essentials were in, #62 P6a) is updated with them on
// Next / Finish (CreateEventWizard.saveMedia).
//
// Highlights live here too. They are event_highlight junction rows, so the step only
// collects ids into the draft and the wizard links / unlinks them on save
// (`highlightChanges` in wizardSetup.ts).
//
// #139 — on the QUICK headcount path this is the last step: the cover PHOTO only (no video, no highlights), and a card
// saying what Finish does — the event goes live with its link — and what the quick path left for later.
// #148 — on the quick ticket sale too. Its card says whether Finish PUBLISHES (the ticket is free, or card payments are
// on) or keeps the event a draft (a paid ticket nobody could pay for yet), with the card-payments line to fix it here.

import React, { useState } from 'react';
import { Stack, Text, Card, Group, ThemeIcon, Button, SimpleGrid, Alert, ActionIcon } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useAppEnvironment } from '@/contexts';
import { HighlightsProvider } from '@/contexts';
import { WHOLE_SET } from '@/utils/api';
import { AppIcon, MediaSelector, MediaHero, MediaIntent, MediaSelectorResult } from '@/components/generic';
import { CoverFramePicker, EventHighlightsField, canPickFrameFrom } from '@/components/features/Events';
import { eventContextPath } from '@/utils/api';

import { useEventWizard } from '../WizardContext';
import { CardPaymentsNotice } from '@/components/features/CardPayments';
import { QUICK_FINISH_CHECKING, QUICK_PUBLISH_HOLD, quickFinishState } from '../wizardSetup';
import { EVENT_MODE, QUICK_KIND, WizardMediaRef, headcountPartyCap, quickKindOf } from '../wizardTypes';

interface MediaSlot {
  key: 'cover-image' | 'cover-video';
  draftKey: 'coverImage' | 'coverVideo';
  intent: MediaIntent;
  icon: string;
  title: string;
  hint: string;
}

const MEDIA_SLOTS: MediaSlot[] = [
  {
    key: 'cover-image',
    draftKey: 'coverImage',
    intent: 'cover-image',
    icon: 'Photo',
    title: 'Cover photo',
    hint: 'Upload, edit, or paste an image URL',
  },
  {
    key: 'cover-video',
    draftKey: 'coverVideo',
    intent: 'cover-video',
    icon: 'Video',
    title: 'Cover video',
    hint: 'Upload, or paste a YouTube / Vimeo / direct video URL',
  },
];

export const MediaStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const { isMobile } = useAppEnvironment();
  const { draft, patchDraft, cardPayments } = useEventWizard();

  const isInvite = draft.eventMode === EVENT_MODE.MANAGED_INVITES;
  const quick = !!draft.quick;
  const selling = quickKindOf(draft) === QUICK_KIND.TICKETS;
  // #148 — the same answer the Finish button gives (`quickFinishState`): checking, held for cards, held unknown, or live.
  const finishState = quickFinishState(draft, cardPayments);
  const held = finishState === QUICK_PUBLISH_HOLD.CARDS || finishState === QUICK_PUBLISH_HOLD.UNKNOWN;
  const slots = quick ? MEDIA_SLOTS.filter((slot) => slot.key === 'cover-image') : MEDIA_SLOTS;

  const [openSlot, setOpenSlot] = useState<MediaSlot | null>(null);
  // Any frame of the cover VIDEO as the cover PHOTO — what guests see before it plays (`CoverFramePicker`).
  const [framePickerOpen, setFramePickerOpen] = useState(false);
  const frameFromVideo = !quick && canPickFrameFrom(draft.coverVideo?.url);
  const frameButton = (
    <Button size="md" variant="subtle" leftSection={<AppIcon iconName="Video" size={16} />} onClick={() => setFramePickerOpen(true)}>
      {t('events:details.frameFromVideo', { defaultValue: 'Choose a frame from the video' })}
    </Button>
  );

  const handleSelect = (slot: MediaSlot) => (result: MediaSelectorResult) => {
    const ref: WizardMediaRef = { uploadedFileId: result.uploadedFileId ?? null, url: result.url };
    patchDraft({ [slot.draftKey]: ref });
  };

  const clearPick = (slot: MediaSlot) => {
    patchDraft({ [slot.draftKey]: null });
  };

  return (
    <Stack gap="lg">
      {quick ? (
        <Text size="md" c="dimmed">
          {t('events:wizard.quick.mediaIntro', {
            defaultValue: 'Add a picture for the top of your event page. It is optional — you can add one later.',
          })}
        </Text>
      ) : (
        <Text size="sm" c="dimmed">
          {t('events:wizard.media.intro', {
            defaultValue:
              'Add a cover photo or video to make your event stand out. This step is optional — you can skip it and add media anytime.',
          })}
        </Text>
      )}

      {isInvite && (
        <Alert variant="light" color="yellow" icon={<AppIcon iconName="InfoCircle" size={18} />}>
          {t('events:wizard.media.inviteVideoNote', {
            defaultValue:
              'Heads up: videos don’t play inside email or text invites. Recipients see your cover PHOTO in the message preview — the video only plays when they open the event website. So add a strong cover photo even if you use a video.',
          })}
        </Alert>
      )}

      <SimpleGrid cols={isMobile || slots.length === 1 ? 1 : 2} spacing="md">
        {slots.map((slot) => {
          const pick = draft[slot.draftKey];

          return (
            <Card
              key={slot.key}
              withBorder
              radius="md"
              padding="lg"
              style={{ borderStyle: pick ? 'solid' : 'dashed' }}
            >
              {pick ? (
                <Stack gap="sm">
                  <MediaHero
                    radius={8}
                    imageUrl={slot.intent === 'cover-image' ? pick.url : undefined}
                    videoUrl={slot.intent === 'cover-video' ? pick.url : undefined}
                    alt={slot.title}
                  />

                  <Group justify="space-between">
                    <Text fw={600} size="md">
                      {slot.title}
                    </Text>
                    <Group gap="xs">
                      <Button size="md" variant="light" onClick={() => setOpenSlot(slot)}>
                        {t('common:buttons.replace', { defaultValue: 'Replace' })}
                      </Button>
                      <ActionIcon variant="subtle" color="red" onClick={() => clearPick(slot)}>
                        <AppIcon iconName="Trash" size={16} />
                      </ActionIcon>
                    </Group>
                  </Group>
                  {slot.draftKey === 'coverImage' && frameFromVideo ? frameButton : null}
                </Stack>
              ) : (
                <Stack gap="sm" align="center" ta="center">
                  <ThemeIcon variant="light" color="brand" size={48} radius="xl">
                    <AppIcon iconName={slot.icon} size={24} />
                  </ThemeIcon>
                  <Stack gap={2} align="center">
                    <Text fw={600} size="md">
                      {slot.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {slot.hint}
                    </Text>
                  </Stack>
                  <Button
                    size="md"
                    variant="light"
                    leftSection={<AppIcon iconName="Plus" size={14} />}
                    onClick={() => setOpenSlot(slot)}
                  >
                    {t('events:wizard.media.add', { defaultValue: 'Add' })}
                  </Button>
                  {slot.draftKey === 'coverImage' && frameFromVideo ? frameButton : null}
                </Stack>
              )}
            </Card>
          );
        })}
      </SimpleGrid>

      {/* #139 — what Finish does on the quick path, said before the host presses it. */}
      {quick ? (
        <Card withBorder radius="md" padding="lg">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon variant="light" color="teal" size={36} radius="md">
              <AppIcon iconName="Send" size={18} />
            </ThemeIcon>
            <Stack gap={4}>
              <Text fw={600} size="md">
                {t('events:wizard.quick.finishTitle', { defaultValue: 'When you finish' })}
              </Text>
              <Text size="md" c="dimmed">
                {!selling
                  ? t('events:wizard.quick.finishBody', {
                      defaultValue:
                        'Your event goes live and you get its link and QR code to share. Guests reply with how many are coming — up to {{count}} on one reply. Add the place, questions or a guest limit any time from the event’s dashboard.',
                      count: headcountPartyCap(draft.rsvpGroupSize),
                    })
                  : finishState === QUICK_FINISH_CHECKING
                    ? t('events:wizard.quickTickets.finishCheckingBody', {
                        defaultValue: 'Checking whether card payments are set up — your ticket goes on sale only if buyers can pay for it.',
                      })
                    : finishState === QUICK_PUBLISH_HOLD.CARDS
                      ? t('events:wizard.quickTickets.finishDraftBody', {
                          defaultValue:
                            'Your event and its ticket are saved as a draft: buyers could not pay for a ticket until card payments are set up. Set them up below, or publish later from the event’s dashboard.',
                        })
                      : finishState === QUICK_PUBLISH_HOLD.UNKNOWN
                        ? t('events:wizard.quickTickets.finishUnknownBody', {
                            defaultValue:
                              'We could not check whether card payments are set up, so your event and its ticket would be saved as a draft. Check again below before you finish.',
                          })
                        : t('events:wizard.quickTickets.finishBody', {
                        defaultValue:
                          'Your event goes live with “{{ticket}}” on sale, and you get its link and QR code to share. Add more ticket types, discount codes or questions any time from the event’s dashboard.',
                        ticket: draft.quickTicket?.name?.trim() ?? '',
                      })}
              </Text>
            </Stack>
          </Group>
        </Card>
      ) : null}

      {/* #148 — a paid ticket nobody could pay for keeps the event a draft: what to do about it, before Finish. */}
      {quick && selling && held ? (
        <CardPaymentsNotice
          payments={cardPayments}
          paid
          offBody={t('events:wizard.quickTickets.cardsOff', {
            defaultValue:
              'Free tickets work now. To sell paid tickets online, connect your Stripe account — the money goes straight to you. Until then, Finish saves your event as a draft instead of publishing it.',
          })}
          unknownBody={t('events:wizard.quickTickets.cardsUnknown', {
            defaultValue: 'We could not check whether card payments are set up, so Finish would save your event as a draft. Check again before you finish.',
          })}
        />
      ) : null}

      {/* Highlights — the same field the Details page uses, so the two can't drift. */}
      {quick ? null : (
        <Card withBorder radius="md" padding="lg">
          <Stack gap="sm">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" color="grape" size={36} radius="md">
                <AppIcon iconName="Sparkles" size={18} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={600} size="md">
                  {t('events:wizard.media.highlights', { defaultValue: 'Highlights' })}
                </Text>
                <Text size="xs" c="dimmed">
                  {t('events:wizard.media.highlightsHint', {
                    defaultValue: 'What guests should know at a glance — parking, kid friendly, dress code…',
                  })}
                </Text>
              </Stack>
            </Group>

            <HighlightsProvider pageSize={WHOLE_SET}>
              <EventHighlightsField
                value={draft.highlightIds}
                onChange={(highlightIds) => patchDraft({ highlightIds })}
              />
            </HighlightsProvider>
          </Stack>
        </Card>
      )}

      {frameFromVideo ? (
        <CoverFramePicker
          opened={framePickerOpen}
          onClose={() => setFramePickerOpen(false)}
          videoUrl={draft.coverVideo?.url}
          contextPath={draft.createdEventId ? eventContextPath(draft.createdEventId) : null}
          onPicked={(picked) => patchDraft({ coverImage: picked })}
        />
      ) : null}

      {openSlot && (
        <MediaSelector
          opened={openSlot !== null}
          onClose={() => setOpenSlot(null)}
          intent={openSlot.intent}
          value={
            draft[openSlot.draftKey]
              ? { uploadedFileId: draft[openSlot.draftKey]?.uploadedFileId, url: draft[openSlot.draftKey]?.url }
              : null
          }
          onSelect={handleSelect(openSlot)}
        />
      )}
    </Stack>
  );
};
